import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { TaskModal } from '../components/crm/TaskModal';
import { priorityMeta, fmtDate, contactName } from '../components/crm/constants';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'week', label: 'This Week' },
  { key: 'completed', label: 'Completed' },
];

export default function SalesTasks() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await crmFetch(`/tasks?filter=${filter}`);
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setTasks(res.data || []); setTableMissing(false); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggleTask = async (task) => {
    const status = task.status === 'completed' ? 'open' : 'completed';
    const res = await crmFetch(`/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    if (res.success) { toast.success(status === 'completed' ? 'Task completed' : 'Task reopened'); load(); }
    else toast.error(res.message || 'Failed to update task');
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    const res = await crmFetch(`/tasks/${task.id}`, { method: 'DELETE' });
    if (res.success) { toast.success('Task deleted'); load(); }
  };

  const statusBadge = (t) => {
    if (t.status === 'completed') return <Badge className="bg-emerald-100 text-emerald-700 border-0">Completed</Badge>;
    if (t.overdue) return <Badge className="bg-rose-100 text-rose-700 border-0">Overdue</Badge>;
    if (t.status === 'in_progress') return <Badge className="bg-blue-100 text-blue-700 border-0">In Progress</Badge>;
    return <Badge className="bg-slate-100 text-slate-600 border-0">Open</Badge>;
  };

  return (
    <SalesLayout>
      <div data-testid="sales-tasks-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Tasks</h1>
            <p className="text-sm text-gray-500 mt-1">Follow-ups auto-created from conversations, plus your own to-dos.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setModalOpen(true)} data-testid="new-task-btn">
            <Plus className="w-4 h-4 mr-2" /> New Task
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              CRM tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-08-30-crm-phase1.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="flex gap-1 mb-4">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    filter === f.key ? 'bg-blue-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`tasks-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 w-10"></th>
                        <th className="px-4 py-3 font-medium">Task</th>
                        <th className="px-4 py-3 font-medium">Account</th>
                        <th className="px-4 py-3 font-medium">Contact</th>
                        <th className="px-4 py-3 font-medium">Due Date</th>
                        <th className="px-4 py-3 font-medium">Priority</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
                      {!loading && tasks.length === 0 && (
                        <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No tasks in this view.</td></tr>
                      )}
                      {tasks.map((t) => (
                        <tr key={t.id} className={`border-b last:border-0 hover:bg-gray-50 ${t.status === 'completed' ? 'opacity-50' : ''}`} data-testid={`task-row-${t.id}`}>
                          <td className="px-4 py-3">
                            <Checkbox checked={t.status === 'completed'} onCheckedChange={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`} />
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-medium text-gray-900 ${t.status === 'completed' ? 'line-through' : ''}`}>{t.title}</span>
                          </td>
                          <td className="px-4 py-3">
                            {t.account
                              ? <Link to={`/accounts/${t.account.id}`} className="text-blue-600 hover:underline">{t.account.name}</Link>
                              : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{t.contact ? contactName(t.contact) : '—'}</td>
                          <td className={`px-4 py-3 ${t.overdue ? 'text-rose-600 font-semibold' : 'text-gray-600'}`}>
                            {t.due_date ? fmtDate(t.due_date) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${priorityMeta(t.priority).className} border-0`}>{priorityMeta(t.priority).label}</Badge>
                          </td>
                          <td className="px-4 py-3">{statusBadge(t)}</td>
                          <td className="px-4 py-3">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-rose-600" onClick={() => deleteTask(t)} data-testid={`task-delete-${t.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <TaskModal open={modalOpen} onOpenChange={setModalOpen} onSaved={load} />
    </SalesLayout>
  );
}
